import { t, Trans } from "@lingui/macro";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { Link } from "react-router-dom";

import { mail } from "../lib/queries";
import { useLastChart } from "../lib/useLastChart";
import { Button2, Input, Notice, Page, Textarea } from "../ui/Shared";
import { Label, PageTitle, SectionTitle } from "../ui/Typography";
import Spinner from "./Spinner";

type FormData = { from: string; text: string };

const msg = {
  to: process.env.REACT_APP_FEEDBACK_TO as string,
  subject: "Flowchart Fun Feedback",
};

export default function Feedback() {
  const { register, handleSubmit, reset, watch } = useForm<FormData>();
  const fields = watch();
  const isValid = fields.from && fields.text;
  const [success, setSuccess] = useState(false);
  const {
    error,
    isLoading,
    mutate: send,
  } = useMutation({
    mutationFn: mail,
    onSuccess: () => {
      setSuccess(true);
      reset();
    },
  });
  const onSubmit = useCallback(
    (data: FormData) => send({ ...msg, ...data }),
    [send]
  );
  return (
    <Page data-testid="feedback" size="sm" className="gap-8">
      <header className="grid gap-4">
        <PageTitle className="text-center">
          <Trans>Feedback</Trans>
        </PageTitle>
        <p className="text-center text-neutral-500 dark:text-neutral-400">
          <Trans>
            Found a bug? Have a feature request? We would love to hear from you!
          </Trans>
        </p>
      </header>
      {success ? (
        <Success />
      ) : (
        <form
          data-testid="feedback-form"
          className="grid gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <section className="grid gap-2">
            <Label>
              <Trans>Email</Trans>
            </Label>
            <Input
              type="email"
              {...register("from", { required: true })}
              data-testid="email"
            />
          </section>
          <section className="grid gap-2">
            <Label>
              <Trans>Comment</Trans>
            </Label>
            <Textarea
              rows={4}
              {...register("text", { required: true })}
              data-testid="message"
            />
          </section>
          <Button2 disabled={!isValid} color="blue" size="md">
            <Trans>Submit</Trans>
          </Button2>
          {error instanceof Error && (
            <div className="justify-self-center">
              <Notice>
                {t`An error occurred. Try resubmitting or email ${process.env.REACT_APP_FEEDBACK_TO} directly.`}
              </Notice>
            </div>
          )}
        </form>
      )}
      {isLoading && (
        <div className="justify-self-center">
          <Spinner className="text-blue-400 dark:text-blue-500" />
        </div>
      )}
    </Page>
  );
}

function Success() {
  const lastChart = useLastChart((s) => s.lastChart);
  return (
    <div className="grid gap-4 text-center border rounded p-4 border-neutral-400 max-w-4xl mx-auto">
      <SectionTitle isUnderline={false}>
        <Trans>Thank you for your feedback!</Trans>
      </SectionTitle>
      <Link to={lastChart} className="underline text-sm">
        <Trans>Back To Editor</Trans>
      </Link>
    </div>
  );
}
